import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Pencil, Trash2, Globe, Star,
  Download, X
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../supabase/supabase';
import ReactQuill from 'react-quill';

// Estrelas clicáveis (0-5). `value` é a nota do usuário atual (ou null se
// nunca votou); `onRate` recebe o novo valor. Puramente decorativo se
// `readOnly` (usado para exibir a média, não para votar).
const StarRating = ({ value = 0, onRate, readOnly = false, size = 'w-4 h-4' }) => {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div className={`flex items-center gap-0.5 ${readOnly ? '' : ''}`} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={(e) => { e.stopPropagation(); onRate?.(n); }}
          onMouseEnter={() => !readOnly && setHover(n)}
          className={readOnly ? 'cursor-default' : 'cursor-pointer'}
          aria-label={`Avaliar com ${n} estrela${n > 1 ? 's' : ''}`}
        >
          <Star className={`${size} ${n <= display ? 'text-navy fill-navy' : 'text-line fill-line'}`} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
};

const SORT_OPTIONS = [
  { id: 'default', label: 'Recomendados' },
  { id: 'rating', label: 'Mais estrelas' },
  { id: 'downloads', label: 'Mais baixados' },
];

export default function Templates() {
  const { templates, fetchTemplates } = useData();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Link "Pesquisar modelos da comunidade" (TemplatePicker, ao criar laudo)
  // manda pra cá com ?tab=community — respeita só na carga inicial.
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'community' ? 'community' : 'my');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('default');
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [communityTemplates, setCommunityTemplates] = useState([]);
  const [isLoadingCommunity, setIsLoadingCommunity] = useState(false);
  const [myRatings, setMyRatings] = useState({}); // template_id -> nota do usuário atual

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    is_public: false,
    category: 'medico',
    specialty: ''
  });

  const fetchCommunityTemplates = useCallback(async () => {
    setIsLoadingCommunity(true);
    try {
      // Padrão: mais usados primeiro, empate resolvido por maior nota —
      // "dos mais usados e com maior nota" (comportamento pedido).
      let query = supabase.from('templates').select('*').eq('is_public', true);
      if (sortMode === 'rating') {
        query = query.order('rating_avg', { ascending: false }).order('usage_count', { ascending: false });
      } else if (sortMode === 'downloads') {
        query = query.order('usage_count', { ascending: false });
      } else {
        query = query.order('usage_count', { ascending: false }).order('rating_avg', { ascending: false });
      }

      const { data, error } = await query;
      if (error) throw error;
      setCommunityTemplates(data || []);

      // Notas que o usuário atual já deu, para pré-selecionar as estrelas.
      if (user?.id && data?.length) {
        const { data: ratings } = await supabase
          .from('template_ratings')
          .select('template_id, rating')
          .eq('user_id', user.id)
          .in('template_id', data.map(t => t.id));
        setMyRatings(Object.fromEntries((ratings || []).map(r => [r.template_id, r.rating])));
      }
    } catch (err) {
      console.error('Erro ao buscar templates da comunidade:', err);
    } finally {
      setIsLoadingCommunity(false);
    }
  }, [sortMode, user?.id]);

  useEffect(() => {
    if (activeTab === 'community') {
      fetchCommunityTemplates();
    }
  }, [activeTab, fetchCommunityTemplates]);

  // Avaliação 0-5: upsert em template_ratings (1 voto por usuário por
  // template — trocar a nota atualiza em vez de duplicar). O trigger no
  // banco recalcula rating_avg/rating_count automaticamente.
  const handleRate = async (templateId, rating) => {
    if (!user?.id) return;
    setMyRatings(prev => ({ ...prev, [templateId]: rating })); // otimista
    try {
      const { error } = await supabase
        .from('template_ratings')
        .upsert({ template_id: templateId, user_id: user.id, rating }, { onConflict: 'template_id,user_id' });
      if (error) throw error;
      await fetchCommunityTemplates();
    } catch (err) {
      console.error('Erro ao avaliar modelo:', err);
    }
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || '',
        content: template.content || '',
        is_public: template.is_public || false,
        category: template.category || 'medico',
        specialty: template.specialty || ''
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        description: '',
        content: '',
        is_public: false,
        category: user?.type || 'medico',
        specialty: user?.specialty || ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        user_id: user.id,
        author_name: user.name,
        updated_at: new Date().toISOString()
      };

      if (editingTemplate) {
        const { error } = await supabase
          .from('templates')
          .update(payload)
          .eq('id', editingTemplate.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('templates')
          .insert([payload]);
        if (error) throw error;
      }

      await fetchTemplates();
      setShowModal(false);
    } catch (err) {
      alert('Erro ao salvar modelo: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este modelo?')) return;

    try {
      const { error } = await supabase
        .from('templates')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      await fetchTemplates();
    } catch (err) {
      alert('Erro ao excluir: ' + err.message);
    }
  };

  const handleImport = async (template) => {
    try {
      setIsSubmitting(true);
      const { error: insertError } = await supabase
        .from('templates')
        .insert([{
          name: `${template.name} (Cópia)`,
          content: template.content,
          description: template.description,
          user_id: user.id,
          is_public: false,
          category: template.category,
          specialty: template.specialty
        }]);

      if (insertError) throw insertError;

      // Incrementar contador de uso
      await supabase.rpc('increment_template_usage', { t_id: template.id });

      alert('Modelo importado com sucesso!');
      setActiveTab('my');
      await fetchTemplates();
    } catch (err) {
      alert('Erro ao importar: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTemplates = (activeTab === 'my' ? templates : communityTemplates).filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight">Modelos de laudo</h1>
        <button onClick={() => handleOpenModal()} className="btn-primary">
          Novo modelo
        </button>
      </div>

      {/* Tabs */}
      <div className="flex bg-paper-alt p-1.5 rounded-card mb-8 w-fit border border-line">
        <button
          onClick={() => setActiveTab('my')}
          className={`flex items-center gap-2 px-6 py-2.5 min-h-[44px] rounded-card font-semibold text-sm transition-colors ${
            activeTab === 'my'
              ? 'bg-paper text-navy shadow-card'
              : 'text-muted hover:text-ink'
          }`}
        >
          Meus modelos
        </button>
        <button
          onClick={() => setActiveTab('community')}
          className={`flex items-center gap-2 px-6 py-2.5 min-h-[44px] rounded-card font-semibold text-sm transition-colors ${
            activeTab === 'community'
              ? 'bg-paper text-navy shadow-card'
              : 'text-muted hover:text-ink'
          }`}
        >
          Comunidade
        </button>
      </div>

      {/* Busca + ordenação (ordenação só faz sentido na Comunidade) */}
      <div className="bg-paper rounded-card shadow-card border border-line p-5 mb-8 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted w-5 h-5" aria-hidden="true" />
          <label htmlFor="template-search" className="sr-only">Buscar por nome</label>
          <input
            id="template-search"
            type="text"
            placeholder="Buscar por nome..."
            className="input-field pl-12"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {activeTab === 'community' && (
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            className="input-field w-auto sm:w-56"
            aria-label="Ordenar modelos da comunidade"
          >
            {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        )}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {isLoadingCommunity ? (
            Array(6).fill(0).map((_, i) => (
              <div key={i} className="bg-paper rounded-card border border-line p-6 animate-pulse">
                <div className="h-4 bg-paper-alt rounded w-3/4 mb-4" />
                <div className="h-3 bg-paper-alt rounded w-full mb-2" />
                <div className="h-3 bg-paper-alt rounded w-5/6 mb-6" />
                <div className="h-10 bg-paper-alt rounded-card" />
              </div>
            ))
          ) : filteredTemplates.length > 0 ? (
            filteredTemplates.map((template) => (
              <motion.div
                key={template.id}
                layout
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-paper rounded-card border border-line p-6 shadow-card hover:border-navy/40 transition-colors group flex flex-col h-full"
              >
                <div className="flex justify-end items-start mb-4">
                  {activeTab === 'my' ? (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleOpenModal(template)}
                        title="Editar modelo"
                        className="p-2.5 min-h-[44px] min-w-[44px] text-muted hover:text-navy hover:bg-paper-alt rounded-card transition-colors"
                        aria-label={`Editar modelo ${template.name}`}
                      >
                        <Pencil className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        title="Excluir modelo"
                        className="p-2.5 min-h-[44px] min-w-[44px] text-muted hover:text-danger hover:bg-danger-tint rounded-card transition-colors"
                        aria-label={`Excluir modelo ${template.name}`}
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-navy bg-paper-alt px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider border border-line">
                      <Star className="w-3 h-3 fill-navy" aria-hidden="true" />
                      {template.rating_avg ? Number(template.rating_avg).toFixed(1) : 'Sem nota'}
                      {template.rating_count > 0 && <span className="text-muted normal-case font-normal">({template.rating_count})</span>}
                    </div>
                  )}
                </div>

                <h3 className="font-display font-bold text-ink mb-2 line-clamp-1 text-base">{template.name}</h3>
                <p className="text-muted text-sm mb-6 line-clamp-2 min-h-[40px] leading-relaxed">
                  {template.description}
                </p>

                {activeTab === 'community' && (
                  <div className="flex items-center justify-between pt-4 border-t border-line mb-3">
                    <span className="text-[10px] text-muted uppercase tracking-widest">Sua avaliação:</span>
                    <StarRating value={myRatings[template.id] || 0} onRate={(n) => handleRate(template.id, n)} />
                  </div>
                )}

                <div className={`mt-auto pt-4 flex items-center justify-between ${activeTab === 'community' ? '' : 'border-t border-line'}`}>
                  <div className="flex items-center gap-2">
                    {activeTab === 'community' ? (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-paper-alt border border-line rounded-full flex items-center justify-center text-[10px] font-semibold text-navy">
                          {template.author_name?.charAt(0) || 'P'}
                        </div>
                        <span className="text-[10px] text-muted font-semibold truncate max-w-[80px]">{template.author_name || 'Profissional'}</span>
                        <span className="text-[10px] text-muted flex items-center gap-0.5 tabular-nums">
                          <Download className="w-3 h-3" aria-hidden="true" /> {template.usage_count || 0}
                        </span>
                      </div>
                    ) : (
                      <span className="text-[10px] text-muted font-semibold uppercase tracking-widest tabular-nums">
                        {new Date(template.updated_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {activeTab === 'community' ? (
                    <button
                      onClick={() => handleImport(template)}
                      disabled={isSubmitting}
                      className="btn-primary text-[10px] uppercase tracking-widest px-4 py-2 min-h-[36px]"
                    >
                      Usar este modelo
                    </button>
                  ) : template.is_public && (
                    <span className="badge-verified">
                      <Globe className="w-3 h-3" aria-hidden="true" />
                      Público
                    </span>
                  )}
                </div>
              </motion.div>
            ))
          ) : (
            /* Estado vazio desenhado */
            <div className="col-span-full py-20 text-center bg-paper rounded-card border-2 border-dashed border-line">
              <h3 className="font-display text-xl font-bold text-ink mb-2">
                {activeTab === 'my' && !searchTerm ? 'Você ainda não criou nenhum modelo' : 'Nenhum modelo encontrado'}
              </h3>
              <p className="text-muted max-w-xs mx-auto mb-6">
                {activeTab === 'my' && !searchTerm
                  ? 'Crie seu primeiro modelo a partir de um laudo existente e reduza o tempo de emissão.'
                  : 'Tente ajustar sua busca ou explore novos modelos na comunidade.'}
              </p>
              {activeTab === 'my' && !searchTerm && (
                <button onClick={() => handleOpenModal()} className="btn-primary">
                  Criar meu primeiro modelo
                </button>
              )}
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Modal de modelo */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="absolute inset-0 bg-navy/50 backdrop-blur-[2px]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="relative bg-paper rounded-card shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden"
            >
              <div className="p-8 border-b border-line flex justify-between items-center bg-paper sticky top-0 z-10">
                <h2 className="font-display text-2xl font-bold text-navy tracking-tight">
                  {editingTemplate ? 'Editar modelo' : 'Novo modelo'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2.5 min-h-[44px] min-w-[44px] text-muted hover:text-ink hover:bg-paper-alt rounded-card transition-colors"
                  aria-label="Fechar"
                >
                  <X className="w-6 h-6" aria-hidden="true" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="tpl-name" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Nome do modelo *</label>
                      <input
                        id="tpl-name"
                        type="text"
                        required
                        placeholder="Ex: Ultrassom Abdominal"
                        className="input-field"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor="tpl-desc" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Descrição curta</label>
                      <textarea
                        id="tpl-desc"
                        rows={3}
                        className="input-field resize-none"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="tpl-category" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Categoria</label>
                        <select
                          id="tpl-category"
                          className="input-field appearance-none cursor-pointer"
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        >
                          <option value="medico">Médico (CRM)</option>
                          <option value="veterinario">Veterinário (CRMV)</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="tpl-specialty" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Especialidade</label>
                        <input
                          id="tpl-specialty"
                          type="text"
                          placeholder="Ex: Cardiologia"
                          className="input-field"
                          value={formData.specialty}
                          onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-5 bg-paper-alt rounded-card border border-line">
                      <div className="flex-1">
                        <h4 className="text-xs font-semibold text-ink uppercase tracking-widest mb-1">Publicar na comunidade</h4>
                        <p className="text-xs text-muted">Outros profissionais poderão ver e importar seu modelo.</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={formData.is_public}
                        aria-label="Publicar na comunidade"
                        onClick={() => setFormData({ ...formData, is_public: !formData.is_public })}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          formData.is_public ? 'bg-navy' : 'bg-line'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                            formData.is_public ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="block text-xs font-semibold text-muted uppercase tracking-widest">Conteúdo estruturado</span>
                    <span className="text-xs text-muted bg-paper-alt px-2 py-1 rounded-md border border-line">Use [ ] para campos variáveis</span>
                  </div>
                  <div className="rich-editor-wrapper">
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={(val) => setFormData({ ...formData, content: val })}
                      modules={quillModules}
                      placeholder="Estruture seu laudo aqui. Deixe as variáveis para preencher no momento do exame."
                    />
                  </div>
                </div>
              </form>

              <div className="p-8 border-t border-line bg-paper-alt flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary text-xs uppercase tracking-widest"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !formData.name}
                  className="btn-primary text-xs uppercase tracking-widest px-10"
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar modelo'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}